import { Ban, Pencil, Plus, Printer, RefreshCw, Wrench } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  CampoDeBusqueda,
  ConfirmModal,
  DataTable,
  FilterBar,
  PageHeader,
  TarjetasDeEstado,
  ViewToggle,
  type Vista,
} from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Interruptor, Modal } from "../../components/ui/primitivos";
import { cn } from "../../lib/cn";
import {
  actualizarImpresora,
  borrarImpresora,
  type CatalogoItem,
  crearImpresora,
  type EstadoImpresora,
  fetchCatalogo,
  fetchImpresoras,
  type Impresora,
} from "../../lib/consola-api";
import { type Orden, ordenarLista, siguienteOrden } from "../../lib/orden";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import {
  ESTADOS_IMPRESORA,
  EstadoImpresoraBadge,
  ETIQUETA_TARJETA,
  ORDEN_ESTADOS,
  TONO_DE_ESTADO,
} from "./estado";
import { accionDeEstado } from "./impresoras";

/**
 * Impresoras — porteada de `pages/printers/` de `imprenta` (SPEC 0003 fase 4).
 *
 * Se reproducen sus dos vistas, las cinco tarjetas que además filtran y el
 * detalle con la carga de trabajo. **Tres diferencias, todas ya decididas:**
 *
 * - El **tipo sale del catálogo** `tipos-impresora`, no de un enum en el
 *   cliente: acá es una FK (ADR 0017), así que el select se llena con lo que
 *   hay dado de alta.
 * - El detalle es un **modal y no una página aparte**. Allá `PrinterDetailPage`
 *   es una ruta; lo que muestra son cuatro datos y las OTs activas, y sacar a
 *   la persona del listado para eso cuesta más de lo que aporta.
 * - **La carga de trabajo se ve siempre**, no en una vista aparte: el listado ya
 *   la trae agregada.
 */

const VISTAS: readonly Vista[] = ["tarjetas", "tabla"];

export function ImpresorasPagina() {
  const { can } = useSession();
  const navegar = useNavigate();
  const [vista, setVista] = useState<Vista>("tarjetas");
  const [filtroEstado, setFiltroEstado] = useState<EstadoImpresora | "TODAS">("TODAS");
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState<Orden | null>(null);
  const [incluirInactivas, setIncluirInactivas] = useState(false);

  const impresoras = useDatos(
    () => fetchImpresoras({ incluirInactivas }),
    String(incluirInactivas),
  );
  const tipos = useDatos(() => fetchCatalogo("tipos-impresora"));

  /**
   * Abrir una impresora es NAVEGAR, no montar un modal (ADR 0064/0066).
   *
   * Con ruta propia la máquina tiene URL: se comparte con el operario que la
   * atiende y se abre en otra pestaña al lado del tablero.
   */
  const abrirImpresora = (i: Impresora) => navegar(`/impresoras/${i.id}`);
  const [editando, setEditando] = useState<Impresora | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrando, setBorrando] = useState<Impresora | null>(null);
  const [error, setError] = useState<string | null>(null);

  const puedeCrear = can("impresoras", "create");
  const puedeEditar = can("impresoras", "update");
  const puedeBorrar = can("impresoras", "delete");

  const todas = impresoras.datos ?? [];

  // El filtrado es local y no una consulta: el parque de una imprenta son
  // decenas de equipos, no miles. `imprenta` hace lo mismo.
  const termino = busqueda.trim().toLowerCase();
  const filtradas = todas.filter((i) => {
    if (filtroEstado !== "TODAS" && i.estado !== filtroEstado) return false;
    if (!termino) return true;
    return (
      i.nombre.toLowerCase().includes(termino) ||
      (i.modelo ?? "").toLowerCase().includes(termino) ||
      (i.tipo ?? "").toLowerCase().includes(termino)
    );
  });

  const listado = ordenarLista(filtradas, orden, (i, campo) => {
    switch (campo) {
      case "nombre":
        return i.nombre;
      case "modelo":
        return i.modelo;
      case "tipo":
        return i.tipo;
      case "estado":
        return i.estado;
      case "carga":
        return i.ordenesActivas;
      default:
        return null;
    }
  });

  const filtrosActivos = (filtroEstado === "TODAS" ? 0 : 1) + (termino ? 1 : 0);

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await impresoras.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  function alternarEstado(estado: EstadoImpresora) {
    setFiltroEstado((actual) => (actual === estado ? "TODAS" : estado));
  }

  const cambiarEstado = async (impresora: Impresora) => {
    const siguiente = accionDeEstado(impresora.estado);
    if (!siguiente) return;
    await accion(() => actualizarImpresora(impresora.id, { estado: siguiente.destino }));
  };

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Impresoras"
        subtitulo="El parque de equipos, su estado y qué está imprimiendo cada uno"
        acciones={
          <>
            <ViewToggle valor={vista} opciones={VISTAS} alCambiar={setVista} />
            {/* «Actualizar» existe en `imprenta` y acá faltaba: el estado de una
                impresora lo cambia quien está parado al lado de la máquina, y
                sin esto la única forma de ver ese cambio es recargar la página.
                No es polling (invariante 11): lo dispara una persona. */}
            <Boton
              variante="secundario"
              onClick={() => void impresoras.recargar()}
              disabled={impresoras.cargando}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("mr-2 inline h-4 w-4", impresoras.cargando && "animate-spin")}
              />
              Actualizar
            </Boton>
            {puedeCrear ? (
              <Boton onClick={() => setCreando(true)}>
                <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Nueva Impresora
              </Boton>
            ) : null}
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {impresoras.error ? <Aviso>{impresoras.error}</Aviso> : null}

      {/* La familia «estado» (ADR 0094): el fondo de cada tarjeta es el mismo
          color del badge de esa impresora, así que la franja de arriba y la
          lista de abajo se leen con el mismo código. */}
      <TarjetasDeEstado
        tarjetas={[
          {
            clave: "TODAS",
            titulo: "Todas",
            valor: todas.length,
            tono: "neutro",
            activa: filtroEstado === "TODAS",
            alClickear: () => setFiltroEstado("TODAS"),
          },
          ...ORDEN_ESTADOS.map((estado) => ({
            clave: estado,
            titulo: ETIQUETA_TARJETA[estado],
            valor: todas.filter((i) => i.estado === estado).length,
            tono: TONO_DE_ESTADO[estado],
            activa: filtroEstado === estado,
            alClickear: () => alternarEstado(estado),
          })),
        ]}
      />

      <FilterBar
        filtrosActivos={filtrosActivos}
        alLimpiar={() => {
          setFiltroEstado("TODAS");
          setBusqueda("");
        }}
        busqueda={
          // Angosto, no de borde a borde: es como está en `imprenta`, y un
          // campo que ocupa toda la barra al lado de un solo interruptor se lee
          // como si el interruptor fuera parte de la búsqueda.
          <CampoDeBusqueda
            valor={busqueda}
            alCambiar={setBusqueda}
            etiqueta="Buscar impresoras"
            placeholder="Buscar por nombre, modelo o tipo..."
            ancho="medio"
          />
        }
      >
        {/* `div` y no `label`: el interruptor de Radix es un `button`, que no es
            un control asociable con `htmlFor`. El nombre accesible se lo pone su
            `aria-label` y el texto queda decorativo — es lo que hace el resto de
            las pantallas. */}
        <div className="flex items-center gap-2 text-neutral-700 text-sm">
          <Interruptor
            aria-label="Incluir dadas de baja"
            checked={incluirInactivas}
            onCheckedChange={setIncluirInactivas}
          />
          <span aria-hidden="true">Incluir dadas de baja</span>
        </div>
      </FilterBar>

      {vista === "tarjetas" ? (
        <TarjetasImpresoras
          impresoras={listado}
          cargando={impresoras.cargando}
          alVer={abrirImpresora}
        />
      ) : (
        <DataTable
          datos={listado}
          orden={orden}
          alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
          clave={(i) => i.id}
          cargando={impresoras.cargando}
          mensajeVacio="No hay impresoras"
          columnas={[
            {
              clave: "nombre",
              ordenarPor: "nombre",
              encabezado: "Nombre",
              className: "font-medium",
              render: (i) => (
                <button type="button" onClick={() => abrirImpresora(i)} className="hover:underline">
                  {i.nombre}
                </button>
              ),
            },
            {
              clave: "modelo",
              ordenarPor: "modelo",
              encabezado: "Modelo",
              className: "text-neutral-600",
              render: (i) => i.modelo ?? "—",
            },
            {
              clave: "tipo",
              ordenarPor: "tipo",
              encabezado: "Tipo",
              render: (i) =>
                i.tipo ? <Etiqueta tono="neutral">{i.tipo}</Etiqueta> : <span>—</span>,
            },
            {
              clave: "estado",
              ordenarPor: "estado",
              encabezado: "Estado",
              render: (i) => <EstadoImpresoraBadge estado={i.estado} tamano="sm" />,
            },
            {
              clave: "carga",
              ordenarPor: "carga",
              encabezado: "Carga",
              className: "text-neutral-600",
              render: (i) =>
                i.ordenesActivas === 0
                  ? "sin trabajo"
                  : `${i.ordenesActivas} ${i.ordenesActivas === 1 ? "OT" : "OTs"}`,
            },
            {
              clave: "notas",
              encabezado: "Notas",
              className: "text-neutral-600",
              // El dato ya se cargaba en el modal y no se veía en ningún lado:
              // «no imprime en A3 desde marzo» es lo que hay que saber ANTES de
              // asignarle una OT, no después de abrirla.
              render: (i) =>
                i.observaciones ? (
                  <span className="block max-w-[14rem] truncate text-sm" title={i.observaciones}>
                    {i.observaciones}
                  </span>
                ) : (
                  <span className="text-neutral-400">—</span>
                ),
            },
            {
              clave: "activa",
              encabezado: "Activa",
              render: (i) => (
                <Etiqueta tono={i.activa ? "ok" : "neutral"}>{i.activa ? "Sí" : "No"}</Etiqueta>
              ),
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (i) => (
                <div className="flex justify-end gap-2">
                  {/* Cambiar el estado sin abrir el formulario, como la tarjeta
                      de allá: una máquina que se rompe se marca en el momento,
                      y si hay que entrar a editarla no se marca. */}
                  {puedeEditar ? <BotonDeEstado impresora={i} alCambiar={cambiarEstado} /> : null}
                  {puedeEditar ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      aria-label={`Editar ${i.nombre}`}
                      title="Editar"
                      onClick={() => setEditando(i)}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                  {puedeBorrar ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Dar de baja ${i.nombre}`}
                      title="Dar de baja"
                      onClick={() => setBorrando(i)}
                    >
                      <Ban className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      )}

      {editando || creando ? (
        <ModalImpresora
          impresora={editando}
          tipos={tipos.datos ?? []}
          onCerrar={() => {
            setEditando(null);
            setCreando(false);
          }}
          onGuardado={async () => {
            setEditando(null);
            setCreando(false);
            await impresoras.recargar();
          }}
        />
      ) : null}

      <ConfirmModal
        abierto={borrando !== null}
        onCerrar={() => setBorrando(null)}
        onConfirmar={() => {
          if (borrando) accion(() => borrarImpresora(borrando.id));
        }}
        titulo={`Dar de baja ${borrando?.nombre ?? ""}`}
        descripcion={
          borrando && borrando.ordenesActivas > 0
            ? `Tiene ${borrando.ordenesActivas} OT(s) activas. El sistema va a rechazar la baja hasta que se reasignen.`
            : "La impresora deja de aparecer, pero las OTs que ya imprimió la siguen nombrando."
        }
        textoConfirmar="Dar de baja"
        variante="destructiva"
      />
    </div>
  );
}

/**
 * Vista de tarjetas.
 *
 * El icono con el punto de estado encima es de `imprenta`: dice el estado antes
 * de leer nada, y el badge al lado lo repite en texto para quien no distingue
 * el color.
 */
function TarjetasImpresoras({
  impresoras,
  cargando,
  alVer,
}: {
  impresoras: readonly Impresora[];
  cargando: boolean;
  alVer: (i: Impresora) => void;
}) {
  if (cargando) {
    return <p className="py-8 text-center text-neutral-500 text-sm">Cargando...</p>;
  }
  if (impresoras.length === 0) {
    return <p className="py-8 text-center text-neutral-500 text-sm">No hay impresoras</p>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {impresoras.map((i) => (
        <button
          key={i.id}
          type="button"
          onClick={() => alVer(i)}
          className="rounded-xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-neutral-300 hover:bg-neutral-50"
        >
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                <Printer className="h-5 w-5 text-neutral-600" />
              </div>
              <span
                aria-hidden="true"
                className={`-bottom-0.5 -right-0.5 absolute h-3 w-3 rounded-full border-2 border-white ${ESTADOS_IMPRESORA[i.estado].punto}`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-neutral-900">{i.nombre}</p>
              <p className="truncate text-neutral-500 text-sm">{i.modelo ?? "sin modelo"}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {i.tipo ? <Etiqueta tono="neutral">{i.tipo}</Etiqueta> : null}
            <EstadoImpresoraBadge estado={i.estado} tamano="sm" />
            {i.activa ? null : <Etiqueta tono="neutral">dada de baja</Etiqueta>}
          </div>

          <p className="mt-3 text-neutral-600 text-sm">
            {i.ordenesActivas === 0
              ? "Sin trabajo asignado"
              : `${i.ordenesActivas} ${i.ordenesActivas === 1 ? "orden activa" : "órdenes activas"}`}
          </p>
        </button>
      ))}
    </div>
  );
}

/** Detalle: los datos del equipo y las OTs que tiene encima ahora. */
function ModalImpresora({
  impresora,
  tipos,
  onCerrar,
  onGuardado,
}: {
  impresora: Impresora | null;
  tipos: readonly CatalogoItem[];
  onCerrar: () => void;
  onGuardado: () => Promise<void>;
}) {
  const [datos, setDatos] = useState({
    nombre: impresora?.nombre ?? "",
    modelo: impresora?.modelo ?? "",
    tipoImpresoraId: impresora?.tipoImpresoraId ?? "",
    observaciones: impresora?.observaciones ?? "",
    estado: (impresora?.estado ?? "IDLE") as EstadoImpresora,
    activa: impresora?.activa ?? true,
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const cuerpo = {
        nombre: datos.nombre.trim(),
        modelo: datos.modelo.trim() || null,
        tipoImpresoraId: datos.tipoImpresoraId || null,
        observaciones: datos.observaciones.trim() || null,
        estado: datos.estado,
        activa: datos.activa,
      };
      if (impresora) await actualizarImpresora(impresora.id, cuerpo);
      else await crearImpresora(cuerpo);
      await onGuardado();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={impresora ? `Editar ${impresora.nombre}` : "Nueva impresora"}
    >
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}

        <Campo
          etiqueta="Nombre"
          value={datos.nombre}
          onChange={(e) => setDatos((d) => ({ ...d, nombre: e.target.value }))}
        />
        <Campo
          etiqueta="Modelo"
          value={datos.modelo}
          onChange={(e) => setDatos((d) => ({ ...d, modelo: e.target.value }))}
        />

        <div className="space-y-1">
          <label htmlFor="tipo-impresora" className="block text-neutral-600 text-sm">
            Tipo
          </label>
          <select
            id="tipo-impresora"
            value={datos.tipoImpresoraId}
            onChange={(e) => setDatos((d) => ({ ...d, tipoImpresoraId: e.target.value }))}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">sin tipo</option>
            {tipos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label htmlFor="estado-impresora" className="block text-neutral-600 text-sm">
            Estado
          </label>
          <select
            id="estado-impresora"
            value={datos.estado}
            onChange={(e) => setDatos((d) => ({ ...d, estado: e.target.value as EstadoImpresora }))}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            {ORDEN_ESTADOS.map((estado) => (
              <option key={estado} value={estado}>
                {ESTADOS_IMPRESORA[estado].etiqueta}
              </option>
            ))}
          </select>
        </div>

        <Campo
          etiqueta="Observaciones"
          value={datos.observaciones}
          onChange={(e) => setDatos((d) => ({ ...d, observaciones: e.target.value }))}
        />

        <div className="flex items-center gap-2 text-neutral-700 text-sm">
          <Interruptor
            aria-label="Impresora activa"
            checked={datos.activa}
            onCheckedChange={(v) => setDatos((d) => ({ ...d, activa: v }))}
          />
          <span aria-hidden="true">Activa (aparece para asignar trabajo)</span>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton onClick={guardar} disabled={guardando || datos.nombre.trim() === ""}>
          {guardando ? "Guardando..." : "Guardar"}
        </Boton>
      </div>
    </Modal>
  );
}

/** «A mantenimiento» / «Disponible», según dónde esté la máquina. */
function BotonDeEstado({
  impresora,
  alCambiar,
}: {
  impresora: Impresora;
  alCambiar: (i: Impresora) => Promise<void>;
}) {
  const accion = accionDeEstado(impresora.estado);
  if (!accion) return null;
  return (
    <Boton variante="secundario" onClick={() => void alCambiar(impresora)}>
      {accion.etiqueta}
    </Boton>
  );
}
